import DataTable from "datatables.net-react";
import DT from "datatables.net-dt";
import "datatables.net-responsive-dt";
import "datatables.net-select-dt";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import {
  getAllContractTerms,
  deleteContractTerm,
} from "../../../redux/contractSlice/contractSlice";
import Swal from "sweetalert2";
import "./TermsContract.css";

DataTable.use(DT);

export default function TermsContract() {
  const [termsContract, setTermsContract] = useState([]);
  const [allTermsData, setAllTermsData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tableKey, setTableKey] = useState(0);
  const dispatch = useDispatch();
  const { allContractTerms, status } = useSelector((state) => state.contract);
  const table = useRef();

  // جلب كل البيانات
  useEffect(() => {
    dispatch(getAllContractTerms());
  }, [dispatch]);

  useEffect(() => {
    if (status === "succeeded" && allContractTerms) {
      console.log("✅ Loaded contract terms:", allContractTerms.length);
      setAllTermsData(allContractTerms);
      setTermsContract(allContractTerms);
      setLoading(false);
    }
  }, [status, allContractTerms]);

  // البحث المحلي في الـ Frontend
  const searchInFrontend = useCallback((keyword, data) => {
    if (!keyword.trim()) {
      return data;
    }

    const searchTerm = keyword.toLowerCase().trim();

    return data.filter((term) => {
      const title = term.title?.toLowerCase() || "";
      const description = term.description?.toLowerCase() || "";

      return title.includes(searchTerm) || description.includes(searchTerm);
    });
  }, []);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((keyword) => {
      console.log("🔍 Frontend Search for:", keyword);

      if (keyword.trim() !== "") {
        setIsSearching(true);
        const results = searchInFrontend(keyword, allTermsData);
        console.log(`✅ Found ${results.length} results`);
        setTermsContract(results);
        setTableKey((prev) => prev + 1);
      } else {
        setIsSearching(false);
        setTermsContract(allTermsData);
        setTableKey((prev) => prev + 1);
      }
    }, 300),
    [allTermsData, searchInFrontend]
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery("");
    setIsSearching(false);
    setTermsContract(allTermsData);
    setTableKey((prev) => prev + 1);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "هل أنت متأكد؟",
      text: "لن تتمكن من استرجاع هذا البند بعد الحذف!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "نعم، احذف!",
      cancelButtonText: "إلغاء",
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        await dispatch(deleteContractTerm(id)).unwrap();

        // تحديث البيانات بعد الحذف
        const updatedData = allTermsData.filter((term) => term.id !== id);
        setAllTermsData(updatedData);

        // تطبيق البحث على البيانات الجديدة
        if (isSearching) {
          const filteredData = searchInFrontend(searchQuery, updatedData);
          setTermsContract(filteredData);
        } else {
          setTermsContract(updatedData);
        }

        setTableKey((prev) => prev + 1);

        Swal.fire({
          title: "تم الحذف!",
          text: "تم حذف البند بنجاح",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (err) {
        Swal.fire({
          title: "خطأ!",
          text: "حدث خطأ أثناء الحذف",
          icon: "error",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const screenColumns = [
    { title: "عنوان البند", data: "title" },
    { title: "وصف البند", data: "description" },
    {
      title: "عمليات",
      data: null,
      render: (data, type, row) => `
        <div class="d-flex gap-3 justify-content-center align-items-center">
          <a href="/editContractTerms/${row.id}" class="text-decoration-none" title="تعديل">
            <i class="fa-solid fa-pen text-primary"></i>
          </a>
          <button class="delete-btn border-0 bg-transparent" data-id="${row.id}" title="حذف">
            <i class="fa-solid fa-trash text-danger"></i>
          </button>
        </div>
      `,
    },
  ];

  useEffect(() => {
    let mounted = true;

    if (!loading && termsContract.length > 0) {
      const handleClick = (e) => {
        const btn = e.target.closest(".delete-btn");
        if (btn) {
          const id = btn.getAttribute("data-id");
          if (id && mounted) {
            handleDelete(id);
          }
        }
      };

      document.addEventListener("click", handleClick);

      return () => {
        mounted = false;
        document.removeEventListener("click", handleClick);
      };
    }
  }, [loading, termsContract]);

  return (
    <div className="main">
      <div className="container-fluid">
        <div className="row justify-content-between align-items-center mt-3 mb-4">
          {/* عدد شروط العقد */}
          <div className="col-md-3 d-flex justify-content-md-start justify-content-center mb-3">
            <div className="card py-2 px-3">
              <p className="mb-0 primary-100">
                {isSearching ? (
                  <>
                    نتائج البحث:{" "}
                    <span className="primary-100">{termsContract.length}</span>
                    <small
                      className="text-muted d-block"
                      style={{ fontSize: "0.8rem" }}
                    >
                      (من إجمالي {allTermsData.length})
                    </small>
                  </>
                ) : (
                  <>
                    عدد شروط العقد:{" "}
                    <span className="primary-100">{allTermsData.length}</span>
                  </>
                )}
              </p>
            </div>
          </div>

          {/* Search Box */}
          <div className="col-md-5 mb-3">
            <div className="search-box-terms position-relative">
              <input
                type="text"
                placeholder="ابحث بعنوان البند أو الوصف..."
                className="form-control search-input-terms"
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <i className="fas fa-search search-icon-terms"></i>
              {searchQuery && (
                <button
                  className="btn-clear-search-terms"
                  onClick={clearSearch}
                  title="مسح البحث"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
            {isSearching && (
              <div className="search-info-terms text-center mt-2">
                <small className="text-success">
                  <i className="fas fa-check-circle me-1"></i>
                  وجد {termsContract.length} نتيجة للبحث عن "{searchQuery}"
                </small>
              </div>
            )}
          </div>

          {/* زر إضافة شرط العقد */}
          <div className="col-md-3 text-md-end text-center mb-3">
            <Link to="/addContractTerms" className="btn btn-primary text-white">
              <i className="fas fa-plus me-2"></i>
              إضافة شرط العقد
            </Link>
          </div>
        </div>

        {/* Loading Spinner */}
        {loading ? (
          <div className="loading-spinner-terms">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">جاري التحميل...</span>
            </div>
          </div>
        ) : (
          <>
            <div className="table-responsive" style={{ overflowX: "auto" }}>
              {termsContract && termsContract.length > 0 ? (
                <DataTable
                  key={tableKey}
                  data={termsContract}
                  columns={screenColumns}
                  className="display table table-striped table-bordered"
                  options={{
                    responsive: true,
                    searching: false,
                    paging: false,
                    info: false,
                    destroy: true,
                  }}
                  ref={table}
                />
              ) : (
                <div className="text-center py-5">
                  <i className="fas fa-file-contract fa-3x text-muted mb-3"></i>
                  <p className="text-muted">
                    {searchQuery
                      ? `لا توجد نتائج للبحث عن "${searchQuery}"`
                      : "لا توجد شروط عقد"}
                  </p>
                  {searchQuery && (
                    <button
                      className="btn btn-sm btn-primary mt-2"
                      onClick={clearSearch}
                    >
                      <i className="fas fa-times me-2"></i>
                      مسح البحث وعرض كل الشروط
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Results Summary */}
            {termsContract.length > 0 && (
              <div className="results-summary-terms text-center mt-3">
                <p className="text-muted">
                  عرض {termsContract.length} شرط
                  {isSearching && ` من إجمالي ${allTermsData.length}`}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
